import React, { useState } from 'react';
import useIntegratePOSToastStore from '../../../stores/integratePOSToastStore';
import { Checkbox, TextField, IconButton } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ToastSetupGuide from '../../../components/ToastSetupGuide';

// Toast Prerequisites Content
export const ToastPrerequisitesContent = ({ onNext, onBack, onClose }) => {
  const { setToastUserCreated, setFormData, formData } = useIntegratePOSToastStore();

  const [completedSteps, setCompletedSteps] = useState(formData.prerequisiteSteps || {
    checkSubscription: false,
    verifyPermissions: false,
    readyToSetup: false
  });

  const allStepsCompleted = Object.values(completedSteps).every(step => step);

  const handleStepChange = (stepKey, checked) => {
    setCompletedSteps(prev => ({
      ...prev,
      [stepKey]: checked
    }));

    const newSteps = { ...completedSteps, [stepKey]: checked };
    const allCompleted = Object.values(newSteps).every(step => step);
    setFormData('prerequisiteSteps', newSteps);
    setToastUserCreated(allCompleted);
  };

  const handleNext = () => {
    if (allStepsCompleted) {
      onNext();
    }
  };

  return (
    <div className="p-6 relative">
      {/* Close Icon */}
      <IconButton
        aria-label="Close Toast setup"
        onClick={onClose}
        sx={{
          position: 'absolute',
          top: 12,
          right: 12,
          color: 'gray',
          '&:hover': {
            backgroundColor: 'rgba(0, 0, 0, 0.04)',
          },
        }}
      >
        <CloseIcon />
      </IconButton>

      <div className="text-center mb-8">
        <h2 className="text-2xl font-semibold text-gray-900 mb-2">
          Toast API Integration Setup
        </h2>
        <p className="text-gray-600">
          Let's prepare for your Toast API integration
        </p>
      </div>

      <div className="bg-white border-2 border-gray-200 rounded-2xl p-6 mb-6 shadow-sm">
        <h3 className="text-xl font-semibold text-violet-900 mb-6">Prerequisites for Toast API Integration</h3>

        <div className="space-y-6">
          <div className="flex items-start">
            <Checkbox
              inputProps={{ "aria-label": "Verify Toast subscription" }}
              checked={completedSteps.checkSubscription}
              onChange={(e) => handleStepChange('checkSubscription', e.target.checked)}
              sx={{
                color: "#6d28d9",
                "&.Mui-checked": {
                  color: "#6d28d9",
                },
                marginTop: "2px",
                marginRight: "12px",
                padding: "0"
              }}
            />
            <div className="cursor-default">
              <div className="flex">
                <span className="font-semibold text-violet-700 mr-3 flex-shrink-0">1.</span>
                <div>
                  <p className="font-medium text-gray-900">Verify Toast Subscription</p>
                  <p className="text-gray-600 text-sm mt-1">
                    Ensure you have an active <span className="font-medium">Toast Restaurant Management Suite Essentials</span> or higher subscription for all locations you want to integrate.
                  </p>
                  <p className="text-gray-600 text-sm mt-1">
                    Locations without this subscription will appear grayed out and locked during API setup.
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="flex items-start">
            <Checkbox
              inputProps={{ "aria-label": "Confirm 8.4 Manage Integrations permission" }}
              checked={completedSteps.verifyPermissions}
              onChange={(e) => handleStepChange('verifyPermissions', e.target.checked)}
              sx={{
                color: "#6d28d9",
                "&.Mui-checked": {
                  color: "#6d28d9",
                },
                marginTop: "2px",
                marginRight: "12px",
                padding: "0"
              }}
            />
            <div className="cursor-default">
              <div className="flex">
                <span className="font-semibold text-violet-700 mr-3 flex-shrink-0">2.</span>
                <div>
                  <p className="font-medium text-gray-900">Confirm Admin Permissions</p>
                  <p className="text-gray-600 text-sm mt-1">
                    Verify that you have <span className="font-medium">"8.4 Manage Integrations"</span> permission in Toast Web.
                  </p>
                  <p className="text-gray-600 text-sm mt-1">
                    This permission is required to create and manage API credentials.
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="flex items-start">
            <Checkbox
              inputProps={{ "aria-label": "Ready to create API credentials" }}
              checked={completedSteps.readyToSetup}
              onChange={(e) => handleStepChange('readyToSetup', e.target.checked)}
              sx={{
                color: "#6d28d9",
                "&.Mui-checked": {
                  color: "#6d28d9",
                },
                marginTop: "2px",
                marginRight: "12px",
                padding: "0"
              }}
            />
            <div className="cursor-default">
              <div className="flex">
                <span className="font-semibold text-violet-700 mr-3 flex-shrink-0">3.</span>
                <div>
                  <p className="font-medium text-gray-900">Ready to Create API Credentials</p>
                  <p className="text-gray-600 text-sm mt-1">
                    I'm ready to navigate to Toast Web and create Standard API credentials for Rightwork integration.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="flex space-x-3">
        <button
          onClick={onBack}
          className="flex-1 px-4 py-3 rounded-xl font-medium text-base transition-all border-2 border-gray-200 text-gray-700 hover:bg-gray-50"
        >
          Back
        </button>
        <button
          onClick={handleNext}
          disabled={!allStepsCompleted}
          className={`flex-1 px-4 py-3 rounded-xl font-medium text-base transition-all ${
            allStepsCompleted
              ? 'bg-violet-700 hover:bg-violet-800 text-white shadow-lg hover:shadow-xl'
              : 'bg-gray-200 text-gray-400 cursor-not-allowed'
          }`}
        >
          Continue to API Setup
        </button>
      </div>
    </div>
  );
};

// Toast API Credentials Creation Content
export const ToastAPICredentialsContent = ({ onNext, onBack, onClose }) => {
  const { setAPICredentialsCreated, setFormData, formData } = useIntegratePOSToastStore();

  const [completedSteps, setCompletedSteps] = useState(formData.apiSteps || {
    navigateToAPI: false,
    createCredentials: false,
    selectLocations: false,
    confirmCreation: false
  });

  const allStepsCompleted = Object.values(completedSteps).every(step => step);

  const handleStepChange = (stepKey, checked) => {
    setCompletedSteps(prev => ({
      ...prev,
      [stepKey]: checked
    }));

    const newSteps = { ...completedSteps, [stepKey]: checked };
    const allCompleted = Object.values(newSteps).every(step => step);
    setFormData('apiSteps', newSteps);
    setAPICredentialsCreated(allCompleted);
  };

  const handleNext = () => {
    if (allStepsCompleted) {
      onNext();
    }
  };

  return (
    <div className="p-6 relative">
      {/* Close Icon */}
      <IconButton
        aria-label="Close Toast setup"
        onClick={onClose}
        sx={{
          position: 'absolute',
          top: 12,
          right: 12,
          color: 'gray',
          '&:hover': {
            backgroundColor: 'rgba(0, 0, 0, 0.04)',
          },
        }}
      >
        <CloseIcon />
      </IconButton>

      <div className="text-center mb-8">
        <h2 className="text-2xl font-semibold text-gray-900 mb-2">
          Create Toast API Credentials
        </h2>
        <p className="text-gray-600">
          Set up API access credentials in your Toast account
        </p>
      </div>

      <div className="bg-white border-2 border-gray-200 rounded-2xl p-6 mb-6 shadow-sm">
        <h3 className="text-xl font-semibold text-violet-900 mb-6">Generate Toast API Credentials</h3>
        <ToastSetupGuide expanded />

        <div className="space-y-6">
          <div className="flex items-start">
            <Checkbox
              inputProps={{ "aria-label": "Access Toast API management" }}
              checked={completedSteps.navigateToAPI}
              onChange={(e) => handleStepChange('navigateToAPI', e.target.checked)}
              sx={{
                color: "#6d28d9",
                "&.Mui-checked": {
                  color: "#6d28d9",
                },
                marginTop: "2px",
                marginRight: "12px",
                padding: "0"
              }}
            />
            <div className="cursor-default">
              <div className="flex">
                <span className="font-semibold text-violet-700 mr-3 flex-shrink-0">1.</span>
                <div>
                  <p className="font-medium text-gray-900">Access Toast API Management</p>
                  <p className="text-gray-600 text-sm mt-1">
                    In Toast Web: <span className="font-medium bg-violet-100 text-violet-800 px-2 py-1 rounded text-sm">Integrations → Toast API access → Manage credentials</span>
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="flex items-start">
            <Checkbox
              inputProps={{ "aria-label": "Create Standard API credentials with read scopes" }}
              checked={completedSteps.createCredentials}
              onChange={(e) => handleStepChange('createCredentials', e.target.checked)}
              sx={{
                color: "#6d28d9",
                "&.Mui-checked": {
                  color: "#6d28d9",
                },
                marginTop: "2px",
                marginRight: "12px",
                padding: "0"
              }}
            />
            <div className="cursor-default">
              <div className="flex">
                <span className="font-semibold text-violet-700 mr-3 flex-shrink-0">2.</span>
                <div>
                  <p className="font-medium text-gray-900">Create Standard API Credentials</p>
                  <div className="text-gray-600 text-sm mt-1">
                    <p>Click <span className="font-medium bg-violet-100 text-violet-800 px-2 py-1 rounded text-sm">Create new credentials</span> → <span className="font-medium bg-violet-100 text-violet-800 px-2 py-1 rounded text-sm">Standard API</span></p>
                    <p className="mt-1.5">Name: <span className="font-mono bg-white border px-2 py-1 rounded text-sm">Rightwork Integration</span></p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="flex items-start">
            <Checkbox
              inputProps={{ "aria-label": "Select all intended Toast locations" }}
              checked={completedSteps.selectLocations}
              onChange={(e) => handleStepChange('selectLocations', e.target.checked)}
              sx={{
                color: "#6d28d9",
                "&.Mui-checked": {
                  color: "#6d28d9",
                },
                marginTop: "2px",
                marginRight: "12px",
                padding: "0"
              }}
            />
            <div className="cursor-default">
              <div className="flex">
                <span className="font-semibold text-violet-700 mr-3 flex-shrink-0">3.</span>
                <div>
                  <p className="font-medium text-gray-900">Select Your Locations</p>
                  <div className="text-gray-600 text-sm mt-1">
                    <p>Choose <span className="font-medium">All locations</span> or select individual locations to integrate</p>
                    <p className="text-amber-700 mt-1 text-xs">⚠️ Locked locations need Toast Restaurant Management Suite Essentials</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="flex items-start">
            <Checkbox
              inputProps={{ "aria-label": "Save the Client ID and Client Secret" }}
              checked={completedSteps.confirmCreation}
              onChange={(e) => handleStepChange('confirmCreation', e.target.checked)}
              sx={{
                color: "#6d28d9",
                "&.Mui-checked": {
                  color: "#6d28d9",
                },
                marginTop: "2px",
                marginRight: "12px",
                padding: "0"
              }}
            />
            <div className="cursor-default">
              <div className="flex">
                <span className="font-semibold text-violet-700 mr-3 flex-shrink-0">4.</span>
                <div>
                  <p className="font-medium text-gray-900">Copy Your Credentials</p>
                  <div className="text-gray-600 text-sm mt-1">
                    <p>Click <span className="font-medium bg-violet-100 text-violet-800 px-2 py-1 rounded text-sm">Confirm</span> → Copy your <span className="font-medium">Client ID</span> and <span className="font-medium">Client Secret</span></p>
                    <p className="text-amber-700 mt-1 font-medium">⚠️ Save both credentials securely!</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="flex space-x-3">
        <button
          onClick={onBack}
          className="flex-1 px-4 py-3 rounded-xl font-medium text-base transition-all border-2 border-gray-200 text-gray-700 hover:bg-gray-50"
        >
          Back
        </button>
        <button
          onClick={handleNext}
          disabled={!allStepsCompleted}
          className={`flex-1 px-4 py-3 rounded-xl font-medium text-base transition-all ${
            allStepsCompleted
              ? 'bg-violet-700 hover:bg-violet-800 text-white shadow-lg hover:shadow-xl'
              : 'bg-gray-200 text-gray-400 cursor-not-allowed'
          }`}
        >
          Continue to Credential Input
        </button>
      </div>
    </div>
  );
};

// Toast Credential Input Content
export const ToastCredentialInputContent = ({ onBack, onClose, companyId, onIntegrationComplete, onSubmittingChange = () => {} }) => {
  const { setFormData, formData, integrateToast } = useIntegratePOSToastStore();

  const [credentials, setCredentials] = useState({
    clientId: formData.clientId || '',
    clientSecret: formData.clientSecret || ''
  });
  const [showSecret, setShowSecret] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [connected, setConnected] = useState(false);

  const allFieldsCompleted = Object.values(credentials).every(value => value.trim().length > 0);

  const handleCredentialChange = (field, value) => {
    const updatedCredentials = {
      ...credentials,
      [field]: value
    };
    setCredentials(updatedCredentials);
    setFormData(field, value);
  };

  const handleComplete = async () => {
    if (allFieldsCompleted && !isLoading) {
      setIsLoading(true);
      setErrorMessage('');
      onSubmittingChange(true);
      try {
        const result = await integrateToast(companyId);

        if (result.success) {
          if (typeof onIntegrationComplete === 'function') {
            try {
              await onIntegrationComplete();
            } catch (refreshError) {
              // Credential save succeeded; a refresh failure must not offer resubmission.
            }
          }

          setConnected(true);
        } else {
          setErrorMessage(result.error || 'Unable to connect Toast. Check your credentials and try again.');
        }
      } catch (error) {
        setErrorMessage('Unable to connect Toast. Please try again.');
      } finally {
        setIsLoading(false);
        onSubmittingChange(false);
      }
    }
  };

  if (connected) return (
    <div className="p-6" role="status">
      <h2 className="text-2xl font-semibold mb-3">Toast credentials saved</h2>
      <p>Toast data syncs separately and may take time. Review and link or import your locations in Rightwork when they appear.</p>
      <button type="button" onClick={onClose} className="mt-6 w-full px-4 py-3 rounded-xl bg-violet-700 text-white">Done</button>
    </div>
  );

  return (
    <form data-sentry-block aria-label="Toast credentials" onSubmit={(event) => { event.preventDefault(); handleComplete(); }} className="p-6 relative">
      {/* Close Icon */}
      <IconButton
        aria-label="Close Toast setup"
        type="button"
        disabled={isLoading}
        onClick={onClose}
        sx={{
          position: 'absolute',
          top: 12,
          right: 12,
          color: 'gray',
          '&:hover': {
            backgroundColor: 'rgba(0, 0, 0, 0.04)',
          },
        }}
      >
        <CloseIcon />
      </IconButton>

      <div className="text-center mb-8">
        <h2 className="text-2xl font-semibold text-gray-900 mb-2">
          Enter API Credentials
        </h2>
        <p className="text-gray-600">
          Input your Toast API credentials to complete the integration
        </p>
      </div>

      <div className="bg-white border-2 border-gray-200 rounded-2xl p-6 mb-6 shadow-sm">
        <h3 className="text-xl font-semibold text-violet-900 mb-4">Retrieve Your API Credentials</h3>

        <div className="space-y-4 text-sm text-gray-700 mb-6">
          <p>Now that you've created your API credentials in Toast, you need to enter them below.</p>

          <div className="bg-gray-50 rounded-xl p-4 border border-gray-200">
            <h4 className="font-medium text-gray-900 mb-2">If you can't find your credentials:</h4>
            <ol className="space-y-2 text-sm">
              <li className="flex">
                <span className="font-semibold text-violet-700 mr-2">1.</span>
                <span>Go back to <span className="font-medium">Integrations → Toast API access → Manage credentials</span></span>
              </li>
              <li className="flex">
                <span className="font-semibold text-violet-700 mr-2">2.</span>
                <span>Find your "Rightwork Integration" credentials and click the edit icon (pencil)</span>
              </li>
              <li className="flex">
                <span className="font-semibold text-violet-700 mr-2">3.</span>
                <span>Copy the required information from the Credentials page</span>
              </li>
            </ol>
          </div>
        </div>

        <div className="space-y-4">
          <div>
            <label htmlFor="client-id" className="block text-sm font-medium text-gray-700 mb-2">
              Client ID
            </label>
            <TextField
              disabled={isLoading}
              autoComplete="off"
              id="client-id"
              fullWidth
              variant="outlined"
              size="small"
              value={credentials.clientId}
              onChange={(e) => handleCredentialChange('clientId', e.target.value)}
              placeholder="e.g., ToastAPIClient_abc123..."
              InputProps={{
                sx: {
                  backgroundColor: "white",
                  borderRadius: "8px",
                  fontFamily: "monospace",
                  fontSize: "14px",
                  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#6d28d9",
                  },
                },
              }}
            />
            <p className="text-xs text-gray-600 mt-1">
              You can copy this by clicking the "Copy Client ID" button in Toast Web
            </p>
          </div>

          <div>
            <label htmlFor="client-secret" className="block text-sm font-medium text-gray-700 mb-2">
              Client Secret
            </label>
            <div className="relative">
              <TextField
                disabled={isLoading}
                autoComplete="off"
                id="client-secret"
                fullWidth
                variant="outlined"
                size="small"
                type={showSecret ? "text" : "password"}
                value={credentials.clientSecret}
                onChange={(e) => handleCredentialChange('clientSecret', e.target.value)}
                placeholder="Enter your client secret..."
                InputProps={{
                  sx: {
                    backgroundColor: "white",
                    borderRadius: "8px",
                    fontFamily: "monospace",
                    fontSize: "14px",
                    paddingRight: "40px",
                    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#6d28d9",
                    },
                  },
                }}
              />
              <button
                type="button"
                aria-label={showSecret ? "Hide Client Secret" : "Show Client Secret"}
                aria-pressed={showSecret}
                disabled={isLoading}
                onClick={() => setShowSecret(!showSecret)}
                className="absolute right-3 top-2.5 text-gray-500 hover:text-gray-700"
              >
                {showSecret ? (
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.878 9.878L3 3m6.878 6.878L21 21" />
                  </svg>
                ) : (
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                  </svg>
                )}
              </button>
            </div>
            <p className="text-xs text-gray-600 mt-1">
              This is displayed when you first create credentials or when you rotate your secret
            </p>
          </div>
        </div>
      </div>

      <div className="bg-green-50 border border-green-200 rounded-xl p-4 mb-6">
        <div className="flex items-start">
          <svg className="w-5 h-5 text-green-600 mt-0.5 mr-3 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
          </svg>
          <div className="text-sm text-green-800">
            <p className="font-semibold">Security Note:</p>
            <p>Your credentials are securely encrypted and stored. Rightwork follows industry best practices for credential management and API security.</p>
          </div>
        </div>
      </div>

      {errorMessage && <p role="alert" className="text-rose-700 mb-4">{errorMessage}</p>}
      <div className="flex space-x-3">
        <button
          type="button"
          disabled={isLoading}
          onClick={onBack}
          className="flex-1 px-4 py-3 rounded-xl font-medium text-base transition-all border-2 border-gray-200 text-gray-700 hover:bg-gray-50"
        >
          Back
        </button>
        <button
          type="submit"
          disabled={!allFieldsCompleted || isLoading}
          className={`flex-1 px-4 py-3 rounded-xl font-medium text-base transition-all ${
            allFieldsCompleted && !isLoading
              ? 'bg-violet-700 hover:bg-violet-800 text-white shadow-lg hover:shadow-xl'
              : 'bg-gray-200 text-gray-400 cursor-not-allowed'
          }`}
        >
          {isLoading ? (
            <div className="flex items-center justify-center">
              <svg className="w-4 h-4 mr-2 animate-spin" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              Completing...
            </div>
          ) : (
            'Complete Integration'
          )}
        </button>
      </div>
    </form>
  );
};
